import {Component, Input, OnInit} from '@angular/core';
import {SearchService} from '../search.service';
import {ConfigService} from '../config.service';
import {FacetConfig} from '../shared/config-response';
import {Refinement} from "../shared/search-context";
import {Bucket, Facet} from "../shared/facets";
import { combineLatest, pipe } from 'rxjs';
import { map } from 'rxjs/operators';
import { FacetFilter } from './facet-filter';

@Component({
    selector: 'app-facet',
    templateUrl: './facet.component.html',
    styleUrls: ['./facet.component.scss'],
    standalone: false
})
export class FacetComponent implements OnInit {
  @Input() facetKey: string = '';
  @Input() canRefresh = false;

  isLoading: boolean;
  count: number;
  jsonFacet: FacetConfig;
  facet: Facet;
  staticBuckets: Array<Bucket>;
  textFilter: FacetFilter;
  type: string;
  reformat: any;
  isRefreshing = false;

  constructor(private searchService: SearchService, private configService: ConfigService) {
    this.isLoading = false;
    this.count = 0;
    this.jsonFacet = new FacetConfig();
    this.facet = new Facet();
    this.staticBuckets = [];
    this.textFilter = new FacetFilter();
    this.type = '';
    this.reformat = null;
  }

  ngOnInit() {
    this.configService.getConfig().subscribe(config => {
      if(config) {
        this.jsonFacet = config.jsonFacet;
        this.type = this.jsonFacet[this.facetKey].type;
        this.reformat = this.jsonFacet[this.facetKey].reformat;
      }
    });
    combineLatest([this.searchService.currentFacets, this.searchService.currentSearchContext])
      .pipe(map(([currentFacets, currentContext]) => ({currentFacets, currentContext})))
      .subscribe(subscription => {
        this.count = subscription.currentFacets.count;
        this.facet = subscription.currentFacets[this.facetKey] as Facet;
        if (this.type === 'range') {
          this.facet.buckets.forEach((bucket) => {
            const labels = (this.jsonFacet as any)?.[this.facetKey]?.labels;
            const rangeBucket = labels ? labels[bucket.val] : undefined;
            if (rangeBucket) {
              bucket.rangeLabel = rangeBucket.label;
              bucket.rangeFilter = rangeBucket.filter;
            }
          });

          // range facets do not calculate missing buckets, so add a “Blank” bucket when a separate query facet exists
          const missing = subscription.currentFacets[this.facetKey + '-missing'] as Facet | undefined;
          if (missing?.count) {
            const missingBucket = new Bucket();
            missingBucket.val = 'Blank';
            missingBucket.rangeLabel = 'Blank';
            missingBucket.rangeFilter = 'Blank';
            missingBucket.count = missing.count as number;
            if (!this.facet.buckets.find(b => b.val === 'Blank')) {
              this.facet.buckets.push(missingBucket);
              this.facet.buckets.sort((a, b) => b.count - a.count);
            }
          }
        }
        if(this.facet?.missing && this.facet?.missing?.count) {
          const missingBucket = new Bucket();
          missingBucket.val = 'Blank';
          missingBucket.count = this.facet.missing.count;
          if(!this.facet.buckets.find(bucket => {
            return bucket.val === 'Blank';
          })) {
            this.facet.buckets.push(missingBucket);
            this.facet.buckets.sort((a, b) => b.count - a.count);
          }
        }
        this.staticBuckets = [];
        this.textFilter = new FacetFilter();
        if (this.facet && this.facet?.buckets) {
          this.facet.buckets = this.facet.buckets?.filter((bucket: any) => {
            // keep buckets that have results OR are still effectively timed-out
            return bucket.count > 0 || bucket.timed_out === true;
          });

          const cmp = (a: any, b: any) => {
            const aTimeout = this.isTimeout(a);
            const bTimeout = this.isTimeout(b);

            // “true timeouts” (timed_out && count === 0) go last
            if (aTimeout !== bTimeout) return aTimeout ? 1 : -1;

            // otherwise sort by count desc, then label
            const ac = Number(a.count ?? 0);
            const bc = Number(b.count ?? 0);
            if (bc !== ac) return bc - ac;

            return String(a.label ?? '').localeCompare(String(b.label ?? ''));
          };

          this.facet.buckets = this.facet.buckets.sort(cmp);

          this.staticBuckets = [];
          this.facet.buckets.forEach(bucket => {
            this.staticBuckets.push(bucket);
          });
        }
        subscription.currentContext.refine.forEach(refinement => {
          if (refinement.label === this.facetKey) {
            this.facet.buckets.forEach(bucket => {
              if (refinement.type === 'terms' && refinement.value === bucket.val) {
                bucket.checked = 'checked';
              }
              if (refinement.type === 'range' && refinement.value === bucket.rangeFilter) {
                bucket.checked = 'checked';
              }
            });
          }
        });
      });
    this.searchService.isLoading.subscribe(isLoading => {
      this.isLoading = isLoading;
      if (!isLoading && this.isRefreshing) {
        this.isRefreshing = false;
      }
    });
  }

  refine(field: string, value: string) {
    const facet = this.jsonFacet[field]
    if(facet) {
      this.searchService.refine(Refinement.toRefinement(facet.facetField, field, value));
    }
  }

  onFilterChange(text: string) {
    this.textFilter.text = text;
    const needle = (this.textFilter.text || '').toLowerCase();

    if (this.type === 'range') {
      this.staticBuckets.forEach(bucket => {
        const label = (bucket.rangeLabel || '').toLowerCase();
        bucket.filtered = !label.includes(needle);
      });
    } else {
      this.staticBuckets.forEach(bucket => {
        bucket.filtered = !bucket.val.toLowerCase().includes(needle);
      });
    }
    this.facet.buckets = this.staticBuckets.filter(bucket => !bucket.filtered);
  }

  check(bucket: Bucket) {
    this.searchService.clearSelectedItems();
    bucket.checked = bucket.checked === 'checked' ? '' : 'checked';
    this.submit();
  }

  selectAll() {
    for(let i = 0; i < this.facet.buckets.length; i++) {
      this.facet.buckets[i]['checked'] = 'checked';
    }
  }

  clear() {
    for(let i = 0; i < this.facet.buckets.length; i++) {
      this.facet.buckets[i]['checked'] = '';
    }
    const facet = this.jsonFacet[this.facetKey];
    this.searchService.refineMultiple(facet.facetField, new Array<Refinement>());
  }

  submit() {
    const refinements = new Array<Refinement>();
    const facet = this.jsonFacet[this.facetKey];
    if (facet) {
      for (let i = 0; i < this.facet.buckets.length; i++) {
        const b = this.facet.buckets[i];
        if (b.checked === 'checked') {
          if (this.type === 'range') {
            if (b.rangeFilter && b.rangeLabel) {
              refinements.push(
                Refinement.toRefinement(facet.facetField, this.facetKey, b.rangeFilter, 'range', b.rangeLabel)
              );
            }
          } else {
            refinements.push(
              Refinement.toRefinement(facet.facetField, this.facetKey, b.val, 'terms', b.label)
            );
          }
        }
      }
    }
    this.searchService.refineMultiple(facet.facetField, refinements);
  }

  timeoutText(bucket: Bucket): string {
    const t = bucket?.search_time;
    return `Timed out${t ? ` after ${t}s` : ''}. Results may still be returned.`;
  }

  badgeAria(bucket: any): string {
    return this.isTimeout(bucket) ? 'Timed out' : `${bucket?.count ?? 0} results`;
  }

  refresh() {
    if (this.isRefreshing || this.isLoading) return;
    this.isRefreshing = true;
    this.searchService.refresh();
  }

  // Treat as a timeout only if flagged AND still no results
  isTimeout(bucket: any): boolean {
    const raw = (bucket as any)?.count;
    const c = Number.isFinite(raw) ? (raw as number) : parseInt(String(raw ?? '0'), 10) || 0;
    return !!bucket?.timed_out && c === 0;
  }
  trackBucket = (_idx: number, b: any) => b?.val ?? b?.label ?? _idx;

}
